// View models — presentation shapes derived from IssueDto for the widget/rows
// and drawer. Keeps the dumb (OnPush) components free of derivation logic.

import { IssueDto, IssueLifecycleState } from './issue';
import { ContributionDto, ContributionSourceType } from './contribution';

// How much evidence a contribution holds and since when. `key` picks the
// wording, including the singular forms; the component formats the dates.
export interface EvidenceCountVm {
  key: string;
  reports: number;
  sinceUtc: string;
}

// A DVIR defect's hard expiry. Null for anything that never expires.
export interface EvidenceExpiryVm {
  key: string;
  days: number;
  atUtc: string;
}

// Whether the fault is still on the bus, from the live fault-state record.
export interface FaultStateLineVm {
  key: string;
  atUtc: string;
}

// The Issues drawer and vehicle card share the drawer presentation.
// The alert triage dialog keeps the classic presentation.
export const IssueDetailLayouts = { Classic: 'classic', Drawer: 'drawer' } as const;
export type IssueDetailLayout = typeof IssueDetailLayouts[keyof typeof IssueDetailLayouts];

// Async-write lifecycle for a single issue (drives row indicators).
export const WriteStatuses = {
  Idle: 'idle', Submitting: 'submitting', Confirming: 'confirming', Failed: 'failed',
} as const;
export type WriteStatus = typeof WriteStatuses[keyof typeof WriteStatuses];

export type WriteStatusMap = Record<string, WriteStatus>;

// Precomputed row presentation. `issue` is the raw DTO (the drawer seed + writes
// need it); the rest are derived display bits.
export interface IssueRowVm {
  issue: IssueDto;
  id: string;
  displayName: string;
  description: string;
  lifecycleState: IssueLifecycleState;
  lifecycleCss: string;       // 'lc-active' | 'lc-emerging' | 'lc-fading' | 'lc-closed'
  severity: number;
  confidencePct: number;      // 0–100, rounded (display floor applied)
  confidenceMuted: boolean;   // true when below the Active threshold (0.5)
  category: string;
  openedAtUtc: string;
  lastActivityUtc: string;    // most recent contribution firing (fallback: opened)
  signalCount: number;        // distinct contributing sources (grouped)
  reopened: boolean;
  dvirOrigin: boolean;        // a contribution originated from a DVIR defect
  assigneeInitials: string | null;
  assigneeName: string | null;
}



// One contributing-evidence card in the drawer.
export interface EvidenceCardVm {
  displayLabel: string;
  source: ContributionSourceType;
  manual: boolean;            // an operator's own report rather than a machine signal
  metaLine: string;
  evidenceText: string;       // frozen-at-mint sentence; preferred meta line when present
  corroborationCount: number; // debounce-gated re-fires that drove the density climb
  severityAsserted: number;
  codeLine: string;           // the code a technician looks up; empty for DVIR and operator reports
  whenKey: string;            // wording for the line below; empty when the counting line carries the date
  whenAtUtc: string;          // latest corroboration, with the report time as fallback
  thresholdCount: number;     // firings the rule needs; 0 for DVIR and operator reports
  expiry: EvidenceExpiryVm | null;
  decaying: boolean;
  decayReasonKey: string | null;
  lastFiredAtUtc: string;
  closed: boolean;
  closureChipKey: string | null;  // '@repairedDvir' | '@decayed' | '@cleared'
}

// One grouped evidence entry for the drawer: distinct firings of the same source
// (Alert rule / DVIR slug) collapsed into a single card. A recurring alert mints
// a fresh alertId each interval, so the backend appends a separate contribution
// per firing; this regroups them for the operator.
export interface EvidenceGroupVm {
  representative: ContributionDto;   // most-recently-fired member (drives the card's live state)
  occurrences: number;               // total firings across the group (Σ fireCount)
  hasLive: boolean;                  // at least one member still counts (closure empty)
}

// One row in the expanded card's issue-activity timeline: a translated event
// line (lifecycle, reopen, disposition), sorted by time. Notes/comments live
// on the linked Case's feed, not here.
export interface IssueTimelineEntryVm {
  id: string;
  kind: string;                 // ActivityKind
  atUtc: string;
  initiatedBy: string;          // System | User | External
  actorUserId: string | null;   // null for system/external
  actorName: string | null;     // resolved display name
  notable: boolean;
  textKey: string;
  textParams?: Record<string, unknown>;
}
